// Partenaires et territoires (spec § 3) : un administrateur invite les partenaires (toujours créés invités), les
// suspend, et voit les fiches qui leur sont attribuées ; le gestionnaire des abonnements seul règle leur offre, leurs
// pays, régions, zones ou la licence monde. Les collaborateurs du client sont dans « Collaborateurs » : plus de
// conversion d'un collaborateur en partenaire ici (décision du 24/10/2026). Le cloisonnement est fait par la base.
import { projectName } from "./projectText";
import { UserPlus, X } from "lucide-react";
import {
  useCreate,
  useDataProvider,
  useDelete,
  useGetIdentity,
  useGetList,
  useGetMany,
  useLocaleState,
  useNotify,
  useTranslate,
  useCanAccess,
} from "ra-core";
import { useState } from "react";
import { Link, Navigate } from "react-router";
import { Confirm } from "@/components/admin/confirm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { CrmDataProvider } from "../providers/types";
import type {
  Project,
  ProjectAssignment,
  Sale,
  SubscriptionRequest,
  UserTerritory,
} from "../types";
import { InvitePartnerDialog } from "./InvitePartnerDialog";
import { countryName, formatDay } from "./projectUtils";
import { subscriptionLabel, territoryLabel, useZones } from "./territories";

// Écran réservé aux administrateurs (règle `user_territories` de canAccess) : ce composant n'est pas un <List>, le
// contrôle d'accès de react-admin ne s'y applique pas seul. Un partenaire qui tape l'adresse revient à l'accueil.
export const PartnerList = () => {
  const { canAccess, isPending } = useCanAccess({
    resource: "user_territories",
    action: "list",
  });
  if (isPending) return null;
  if (!canAccess) return <Navigate to="/" replace />;
  return <PartnerListContent />;
};

const PartnerListContent = () => {
  const translate = useTranslate();
  const [inviting, setInviting] = useState(false);
  const { data: sales, refetch } = useGetList<Sale & { partner: boolean }>(
    "sales",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "last_name", order: "ASC" },
    },
  );
  const { data: territories, refetch: refetchTerritories } =
    useGetList<UserTerritory>("user_territories", {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "country", order: "ASC" },
    });
  const { data: assignments } = useGetList<ProjectAssignment>(
    "project_assignments",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "assigned_at", order: "DESC" },
    },
  );
  const { data: requests, refetch: refetchRequests } =
    useGetList<SubscriptionRequest>("subscription_requests", {
      filter: { status: "pending" },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "created_at", order: "ASC" },
    });
  // Partenaires seulement, les suspendus à la fin.
  const partners = (sales ?? [])
    .filter((sale) => sale.partner)
    .sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled));
  // Même règle que la base (`manages_subscriptions`) : le gestionnaire désigné seul. Offre, territoires et retrait
  // d'un abonné lui reviennent (bientôt par Stripe) ; l'équipe du client les voit sans les changer.
  const { identity } = useGetIdentity();
  const me = sales?.find((sale) => sale.id === identity?.id);
  const canManage = !!me?.administrator && !!me.subscription_manager;

  return (
    <div className="mt-2 pb-4 flex flex-col gap-4 max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-80">
          <h2 className="text-xl font-semibold">
            {translate("resources.projects.partners.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("resources.projects.partners.intro")}
          </p>
        </div>
        <Button size="sm" onClick={() => setInviting(true)}>
          <UserPlus className="size-4" aria-hidden />
          {translate("resources.projects.partners.invite.title")}
        </Button>
      </div>
      <InvitePartnerDialog
        open={inviting}
        onOpenChange={setInviting}
        onInvited={async () => {
          refetch();
          return true;
        }}
      />
      <CountryOptions />
      {sales && !partners.length && (
        <p className="text-sm text-muted-foreground">
          {translate("resources.projects.partners.none")}
        </p>
      )}
      {partners.map((sale) => (
        <PartnerCard
          key={sale.id}
          sale={sale}
          territories={(territories ?? []).filter(
            (territory) => territory.sales_id === sale.id,
          )}
          assignments={(assignments ?? []).filter(
            (assignment) => assignment.sales_id === sale.id,
          )}
          request={requests?.find((request) => request.sales_id === sale.id)}
          canManage={canManage}
          onChange={() => {
            refetch();
            refetchTerritories();
            refetchRequests();
          }}
        />
      ))}
    </div>
  );
};

// Pays des fiches, proposés à la saisie ; un autre code ISO reste possible (territoire sans fiche encore).
export const CountryOptions = () => {
  const [locale = "fr"] = useLocaleState();
  const { data } = useGetList<{ id: string; nb_projects: number }>(
    "project_countries",
    {
      pagination: { page: 1, perPage: 300 },
      sort: { field: "nb_projects", order: "DESC" },
    },
  );
  return (
    <datalist id="territory-countries">
      {data?.map((country) => (
        <option key={country.id} value={country.id}>
          {countryName(country.id, locale)}
        </option>
      ))}
    </datalist>
  );
};

export const PartnerCard = ({
  sale,
  territories,
  assignments,
  request,
  canManage,
  onChange,
}: {
  sale: Sale & { partner: boolean };
  territories: UserTerritory[];
  assignments: ProjectAssignment[];
  request?: SubscriptionRequest;
  canManage: boolean;
  onChange: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "fr"] = useLocaleState();
  const [busy, setBusy] = useState(false);
  const [deleteOne] = useDelete();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Retrait du partenariat (le compte devient un collaborateur) : pour réparer une erreur de saisie. Celui d'un
  // abonné revient au gestionnaire des abonnements, comme dans la base (`set_partner`).
  const removePartner = async () => {
    setBusy(true);
    const { error } = await getSupabaseClient().rpc("set_partner", {
      sale: sale.id,
      value: false,
    });
    setBusy(false);
    setConfirmRemove(false);
    if (error) notify(error.message, { type: "error" });
    onChange();
  };

  // Suspendre ou réactiver le compte, par la fonction `users` (même chemin que l'écran d'un collaborateur).
  const setDisabled = async (disabled: boolean) => {
    setBusy(true);
    try {
      await dataProvider.salesUpdate(sale.id, {
        email: sale.email,
        first_name: sale.first_name,
        last_name: sale.last_name,
        administrator: false,
        disabled,
      });
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
    setBusy(false);
    onChange();
  };

  const setOffer = async (offer: string, world: boolean) => {
    setBusy(true);
    const { error } = await getSupabaseClient().rpc("set_partner_offer", {
      sale: sale.id,
      offer,
      world,
    });
    setBusy(false);
    if (error) notify(error.message, { type: "error" });
    onChange();
  };
  const subscriber = sale.partner_offer === "subscriber";

  // Accepter une demande : ajoute le pays ou la zone demandés, puis passe en abonné (ce qui clôt la demande).
  const accept = async (request: SubscriptionRequest) => {
    setBusy(true);
    const taken = territories.some((territory) =>
      request.zone
        ? territory.zone === request.zone
        : territory.country === request.country && !territory.region,
    );
    if ((request.zone || request.country) && !taken) {
      const { error } = await getSupabaseClient()
        .from("user_territories")
        .insert(
          request.zone
            ? { sales_id: sale.id, zone: request.zone }
            : { sales_id: sale.id, country: request.country },
        );
      if (error) {
        setBusy(false);
        notify(error.message, { type: "error" });
        return;
      }
    }
    await setOffer("subscriber", request.plan === "world");
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium">
              {sale.first_name} {sale.last_name}
            </div>
            <div className="text-sm text-muted-foreground truncate">
              {sale.email}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {sale.disabled ? (
              <>
                <Badge variant="outline">
                  {translate("resources.projects.partners.suspended")}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => setDisabled(false)}
                >
                  {translate("resources.projects.partners.enable")}
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setDisabled(true)}
              >
                {translate("resources.projects.partners.disable")}
              </Button>
            )}
            {(canManage || !subscriber) && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setConfirmRemove(true)}
              >
                {translate("resources.projects.partners.remove")}
              </Button>
            )}
          </div>
        </div>
        <Confirm
          isOpen={confirmRemove}
          loading={busy}
          title={translate("resources.projects.partners.remove_title", {
            name: `${sale.first_name} ${sale.last_name}`,
          })}
          content={translate("resources.projects.partners.remove_content")}
          confirm={translate("resources.projects.partners.remove")}
          confirmColor="warning"
          onConfirm={removePartner}
          onClose={() => setConfirmRemove(false)}
        />
        {!sale.disabled && (
          <>
            <div className="flex flex-wrap items-center gap-4">
              <Select
                value={sale.partner_offer ?? "invited"}
                disabled={busy || !canManage}
                onValueChange={(offer) => setOffer(offer, false)}
              >
                <SelectTrigger className="w-56" size="sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invited">
                    {translate("resources.projects.offer.invited")}
                  </SelectItem>
                  <SelectItem value="subscriber">
                    {translate("resources.projects.offer.subscriber")}
                  </SelectItem>
                </SelectContent>
              </Select>
              {subscriber && (
                <label className="flex items-center gap-2 text-sm">
                  {translate("resources.projects.offer.world")}
                  <Switch
                    checked={!!sale.partner_world}
                    disabled={busy || !canManage}
                    onCheckedChange={(world) => setOffer("subscriber", world)}
                  />
                </label>
              )}
              {!canManage && (
                <span className="text-xs text-muted-foreground">
                  {translate("resources.projects.partners.offer_locked")}
                </span>
              )}
            </div>
            {request && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
                <span>
                  {translate("resources.projects.pricing.requested", {
                    date: formatDay(request.created_at, locale),
                    label: subscriptionLabel(request, locale, translate),
                  })}
                </span>
                {canManage && (
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => accept(request)}
                  >
                    {translate("resources.projects.pricing.accept")}
                  </Button>
                )}
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              {translate(
                subscriber
                  ? "resources.projects.offer.subscriber_help"
                  : "resources.projects.offer.invited_help",
              )}
            </p>
            <div className="flex flex-col gap-2">
              <span className="text-xs text-muted-foreground tracking-wide">
                {translate("resources.projects.partners.territories")}
              </span>
              <div className="flex flex-wrap gap-2">
                {!territories.length && (
                  <span className="text-sm text-muted-foreground">
                    {translate("resources.projects.partners.no_territory")}
                  </span>
                )}
                {territories.map((territory) => (
                  <Badge
                    key={territory.id}
                    variant="outline"
                    className={`gap-1 ${subscriber && !sale.partner_world ? "" : "opacity-50"}`}
                  >
                    {territoryLabel(territory, locale, translate)}
                    {canManage && (
                      <button
                        type="button"
                        aria-label={translate("ra.action.delete")}
                        onClick={() =>
                          deleteOne(
                            "user_territories",
                            { id: territory.id, previousData: territory },
                            {
                              onSuccess: onChange,
                              mutationMode: "pessimistic",
                            },
                          )
                        }
                      >
                        <X className="size-3" />
                      </button>
                    )}
                  </Badge>
                ))}
              </div>
              {canManage && (
                <>
                  <p className="text-xs text-muted-foreground">
                    {translate("resources.projects.partners.region_hint")}
                  </p>
                  <TerritoryForm saleId={sale.id} onAdded={onChange} />
                  <ZoneForm
                    saleId={sale.id}
                    taken={territories.flatMap((territory) =>
                      territory.zone ? [territory.zone] : [],
                    )}
                    onAdded={onChange}
                  />
                </>
              )}
            </div>
            <AssignedProjects assignments={assignments} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

const TerritoryForm = ({
  saleId,
  onAdded,
}: {
  saleId: Sale["id"];
  onAdded: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [create, { isPending }] = useCreate();
  const code = country.trim().toUpperCase();
  const valid = /^[A-Z]{2}$/.test(code);

  const add = () =>
    create(
      "user_territories",
      {
        data: {
          sales_id: saleId,
          country: code,
          region: region.trim() || null,
        },
      },
      {
        onSuccess: () => {
          setCountry("");
          setRegion("");
          onAdded();
        },
        onError: (error) =>
          notify(error instanceof Error ? error.message : String(error), {
            type: "error",
          }),
      },
    );

  return (
    <form
      className="flex flex-wrap gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) add();
      }}
    >
      <Input
        className="w-28"
        list="territory-countries"
        placeholder={translate("resources.projects.partners.country")}
        value={country}
        onChange={(event) => setCountry(event.target.value)}
        maxLength={2}
      />
      <Input
        className="w-56"
        placeholder={translate("resources.projects.partners.region")}
        value={region}
        onChange={(event) => setRegion(event.target.value)}
      />
      <Button
        type="submit"
        size="sm"
        variant="outline"
        disabled={!valid || isPending}
      >
        {translate("resources.projects.partners.add_territory")}
      </Button>
    </form>
  );
};

const ZoneForm = ({
  saleId,
  taken,
  onAdded,
}: {
  saleId: Sale["id"];
  taken: string[];
  onAdded: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "fr"] = useLocaleState();
  const { data: zones } = useZones();
  const [zone, setZone] = useState("");
  const [create, { isPending }] = useCreate();
  const available = (zones ?? []).filter((item) => !taken.includes(item.code));
  if (!available.length) return null;

  const add = () =>
    create(
      "user_territories",
      { data: { sales_id: saleId, zone } },
      {
        onSuccess: () => {
          setZone("");
          onAdded();
        },
        onError: (error) =>
          notify(error instanceof Error ? error.message : String(error), {
            type: "error",
          }),
      },
    );

  return (
    <div className="flex flex-wrap gap-2">
      <Select value={zone} onValueChange={setZone}>
        <SelectTrigger className="w-[22.5rem]" size="sm">
          <SelectValue
            placeholder={translate("resources.projects.offer.choose_zone")}
          />
        </SelectTrigger>
        <SelectContent>
          {available.map((item) => (
            <SelectItem key={item.code} value={item.code}>
              {translate(`resources.projects.zones.${item.code}`)} (
              {item.countries
                .map((country) => countryName(country, locale))
                .join(", ")}
              )
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        size="sm"
        variant="outline"
        disabled={!zone || isPending}
        onClick={add}
      >
        {translate("resources.projects.offer.add_zone")}
      </Button>
    </div>
  );
};

const AssignedProjects = ({
  assignments,
}: {
  assignments: ProjectAssignment[];
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { data: projects } = useGetMany<Project>(
    "projects",
    { ids: assignments.map((assignment) => assignment.project_id) },
    { enabled: assignments.length > 0 },
  );
  if (!assignments.length) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground tracking-wide">
        {translate("resources.projects.partners.assigned", {
          smart_count: assignments.length,
        })}
      </span>
      <ul className="flex flex-col gap-1">
        {projects?.map((project) => (
          <li key={project.id}>
            <Link
              to={`/projects/${project.id}/show`}
              className="text-sm underline"
            >
              {projectName(project, locale)}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
